import React from 'react';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiTarget,FiEye,FiCheckCircle,FiAward,FiShield,FiUsers,FiTrendingUp,FiHeart,FiClock} = FiIcons;

const AboutUs = () => {
  const values = [
    {
      icon: FiAward,
      title: 'Excellence',
      description: 'High-quality academic support in every service'
    },
    {
      icon: FiShield,
      title: 'Integrity',
      description: 'Honest,original,and ethical assistance'
    },
    {
      icon: FiUsers,
      title: 'Student Success',
      description: 'Every service is focused on building student growth'
    },
    {
      icon: FiTrendingUp,
      title: 'Innovation',
      description: 'Using modern digital tools for better learning'
    },
    {
      icon: FiHeart,
      title: 'Support',
      description: '24/7 availability to help whenever students need us'
    },
    {
      icon: FiClock,
      title: 'Reliability',
      description: 'Consistent,timely delivery and dependable academic assistance'
    }
  ];

  const reasons = [
    'Expert tutors with advanced qualifications to guide you in assignments and studies',
    'Affordable,student-friendly pricing',
    'Confidential and secure services',
    'Results-focused approach that builds both grades and skills'
  ];

  const services = [
    'Assignment Support – Essays,dissertations,case studies,research projects,and academic writing.',
    'Tutoring Services – Personalized one-to-one and group sessions across multiple subjects and levels.',
    'Skill Development – Training in academic writing,structuring,referencing,and research techniques.'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            About Tutor Hub Online – Your Trusted Academic Partner
          </h1>
          <h2 className="text-xl lg:text-2xl text-blue-100 max-w-4xl mx-auto">
            Empowering students with expert assignment support and personalized tutoring to achieve long-term success.
          </h2>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Who We Are</h2>
              <p className="text-gray-600 text-lg">
                At Tutor Hub Online,we believe that education should empower,not overwhelm. Our platform is built to support students with reliable assignment support and tutoring services that make learning more effective,stress-free,and rewarding.
              </p>
              <p className="text-gray-600">
                We are a team of experienced academic professionals and qualified tutors dedicated to guiding students through every academic challenge.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://i.ibb.co/NnVpBZNq/About-Tutor-Hub-Online.jpg"
                alt="About Tutor Hub Online"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiTarget} className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-gray-600">
                👉 To empower students by providing expert academic assistance and interactive learning opportunities that lead to lasting knowledge,confidence,and success.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiEye} className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-gray-600 mb-4">
                We aim to become the most trusted global platform for academic support. At Tutor Hub Online,our vision is to make assignment support and tutoring:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li>• Accessible to every student</li>
                <li>• Affordable without compromising quality</li>
                <li>• Effective in building long-term academic skills</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What We Do</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm border">
                <p className="text-gray-700">{service}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Students Choose Tutor Hub Online</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reasons.map((reason, index) => (
              <div key={index} className="flex items-start space-x-3">
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{reason}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center bg-white p-6 rounded-xl shadow-sm">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            🎯 At Tutor Hub Online,we don't just provide solutions — we provide pathways to success.
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-4xl mx-auto">
            Whether you need urgent assignment support or long-term tutoring support,we're here to guide you every step of the way.
          </p>
          <Link
            to="/contact"
            className="bg-accent text-black px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-colors text-lg"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;